import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout, Sidebar } from '../components/layout';
import { ConfirmDialog } from '../components/ui';
import { PipelineOverview, QuickStats, QueueHeader, QueueFooter } from '../components/dashboard';
import { CaseTable } from '../components/case';
import { useCasesStore } from '../store/casesStore';
import { useJobsStore } from '../store/jobsStore';
import { useJobsPolling, useCasesPolling, useSidebarState, useDashboardState, useUploadedCases, useOtherCases, useQuickStats, useDashboardStats } from '../hooks';
import { casesApi } from '../api';
import type { SalesforceStatusInfo } from '../types';
import type { DashboardSection } from '../components/layout';

const REFRESH_INTERVAL = 30; // seconds

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    cases,
    salesforceCases,
    isFetchingSalesforce,
    fetchSalesforceCases,
    fetchCases,
    downloadCases,
    translateCases,
    uploadCases,
    restartCase,
  } = useCasesStore();
  const { clearPending } = useJobsStore();
  const [refreshCountdown, setRefreshCountdown] = useState(REFRESH_INTERVAL);

  // Sidebar state
  const { isCollapsed, toggle: toggleSidebar } = useSidebarState();

  // Dashboard state (section, pagination, selections, filtering)
  const dashboard = useDashboardState({ cases, salesforceCases });

  // Server-side paginated uploaded cases
  const uploadedCases = useUploadedCases({
    enabled: dashboard.activeSection === 'uploaded',
  });

  // Server-side paginated other cases
  const otherCases = useOtherCases({
    enabled: dashboard.activeSection === 'other',
  });

  // Quick stats and dashboard stats from backend
  const { stats: quickStats } = useQuickStats();
  const { stats: dashboardStats, refresh: refreshDashboardStats } = useDashboardStats();

  // Merge local counts with server-side uploaded/other count for accurate pipeline view
  const pipelineCounts = useMemo(() => ({
    new: dashboard.counts.new,
    queue: dashboard.counts.queue,
    approved: dashboard.counts.approved,
    uploaded: dashboardStats?.uploaded ?? 0,  // Use server-side count for uploaded
    returned: dashboard.counts.returned,
    issues: dashboard.counts.issues,
    other: dashboardStats?.other ?? 0,  // Use server-side count for other
  }), [dashboard.counts, dashboardStats]);

  // Handle section from URL params
  useEffect(() => {
    const sectionParam = searchParams.get('section') as DashboardSection | null;
    if (sectionParam && ['new', 'queue', 'approved', 'uploaded', 'returned', 'issues', 'other'].includes(sectionParam)) {
      dashboard.setActiveSection(sectionParam);
      // Clear the URL param after setting
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, dashboard.setActiveSection]);

  // Ephemeral SF status data (fetched per page)
  const [sfStatusMap, setSfStatusMap] = useState<Record<string, SalesforceStatusInfo>>({});

  // Confirmation dialogs
  const [translateConfirm, setTranslateConfirm] = useState<{
    isOpen: boolean;
    casesToTranslate: string[];
    newCount: number;
    redoCount: number;
  }>({ isOpen: false, casesToTranslate: [], newCount: 0, redoCount: 0 });

  const [restartConfirm, setRestartConfirm] = useState<{
    isOpen: boolean;
    caseNumber: string;
  }>({ isOpen: false, caseNumber: '' });

  // Callback when a job completes - refresh the data and clear pending state
  const handleJobComplete = useCallback(
    (jobId: string, jobType: string) => {
      console.log(`Job ${jobId} (${jobType}) completed, refreshing data...`);

      if (jobType.includes('restart')) {
        clearPending([], 'restart');
        clearPending([], 'download');
      } else if (jobType.includes('download')) {
        clearPending([], 'download');
      } else if (jobType.includes('translate')) {
        clearPending([], 'translate');
      } else if (jobType.includes('upload')) {
        clearPending([], 'upload');
        // Refresh uploaded cases list and dashboard stats after upload
        uploadedCases.refresh();
        refreshDashboardStats();
      }

      fetchCases();
      fetchSalesforceCases();
    },
    [fetchCases, fetchSalesforceCases, clearPending, uploadedCases, refreshDashboardStats]
  );

  // Poll for active jobs (handles spinners and job completion)
  const {
    isDownloading,
    isTranslating,
    isUploading,
    isRestarting,
    startDownload,
    startTranslate,
    startUpload,
    startRestart,
  } = useJobsPolling({ onJobComplete: handleJobComplete });

  // Poll for case status updates (updates UI as individual translations complete)
  useCasesPolling();

  // Set page title
  useEffect(() => {
    document.title = 'TEC | Translator';
  }, []);

  // Fetch Salesforce cases and local cases on mount
  useEffect(() => {
    fetchSalesforceCases();
    fetchCases();
  }, [fetchSalesforceCases, fetchCases]);

  // Auto-refresh every REFRESH_INTERVAL seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setRefreshCountdown((prev) => {
        if (prev <= 1) {
          fetchSalesforceCases();
          fetchCases();
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [fetchSalesforceCases, fetchCases]);

  // Fetch SF status for visible cases in Returned/Issues sections
  // Note: Uploaded section gets SF status from the paginated endpoint directly
  useEffect(() => {
    if (!['returned', 'issues'].includes(dashboard.activeSection)) return;

    const visibleCaseNumbers = dashboard.paginatedCases.map((c) => c.case_number);
    if (visibleCaseNumbers.length === 0) return;

    const fetchSfStatus = async () => {
      try {
        const statusData = await casesApi.getSalesforceStatus(visibleCaseNumbers);
        const newMap: Record<string, SalesforceStatusInfo> = {};
        for (const s of statusData) {
          newMap[s.case_number] = s;
        }
        setSfStatusMap((prev) => ({ ...prev, ...newMap }));
      } catch (error) {
        console.error('Failed to fetch SF status:', error);
      }
    };

    fetchSfStatus();
  }, [dashboard.activeSection, dashboard.paginatedCases]);

  // Handlers
  const handleDownload = async (caseNumber: string) => {
    startDownload([caseNumber]);
    await downloadCases([caseNumber]);
  };

  const handleDownloadSelected = async () => {
    const caseNumbers = Array.from(dashboard.selectedCases);
    dashboard.clearSelection();
    startDownload(caseNumbers);
    await downloadCases(caseNumbers);
  };

  const handleTranslate = async (caseNumber: string) => {
    startTranslate([caseNumber]);
    await translateCases([caseNumber]);
  };

  const handleTranslateAllClick = () => {
    const casesToTranslate =
      dashboard.selectedCases.size > 0
        ? Array.from(dashboard.selectedCases)
        : dashboard.translationQueue
            .filter((c) => ['downloaded', 'failed'].includes(c.status))
            .map((c) => c.case_number);

    if (casesToTranslate.length === 0) return;

    const newCount = casesToTranslate.filter((cn) => {
      const c = cases.find((c) => c.case_number === cn);
      return c && ['downloaded', 'failed'].includes(c.status);
    }).length;
    const redoCount = casesToTranslate.length - newCount;

    setTranslateConfirm({
      isOpen: true,
      casesToTranslate,
      newCount,
      redoCount,
    });
  };

  const handleTranslateConfirm = async () => {
    const { casesToTranslate } = translateConfirm;
    setTranslateConfirm({ isOpen: false, casesToTranslate: [], newCount: 0, redoCount: 0 });
    dashboard.clearSelection();
    startTranslate(casesToTranslate);
    await translateCases(casesToTranslate);
  };

  const handleTranslateCancel = () => {
    setTranslateConfirm({ isOpen: false, casesToTranslate: [], newCount: 0, redoCount: 0 });
  };

  const handleUpload = async (caseNumber: string) => {
    startUpload([caseNumber]);
    await uploadCases([caseNumber]);
  };

  const handleUploadSelected = async () => {
    const casesToUpload =
      dashboard.selectedCases.size > 0
        ? Array.from(dashboard.selectedCases)
        : dashboard.approvedCases.filter((c) => c.status === 'approved').map((c) => c.case_number);
    dashboard.clearSelection();
    startUpload(casesToUpload);
    await uploadCases(casesToUpload);
  };

  const handleRestartClick = (caseNumber: string) => {
    setRestartConfirm({ isOpen: true, caseNumber });
  };

  const handleRestartConfirm = async () => {
    const { caseNumber } = restartConfirm;
    setRestartConfirm({ isOpen: false, caseNumber: '' });
    startRestart([caseNumber]);
    await restartCase(caseNumber);
  };

  const handleRestartCancel = () => {
    setRestartConfirm({ isOpen: false, caseNumber: '' });
  };

  const handleRefresh = () => {
    fetchSalesforceCases();
    fetchCases();
    setRefreshCountdown(REFRESH_INTERVAL);
  };

  // Get columns for current section
  const getColumns = (): ('checkbox' | 'caseNumber' | 'typeOfAnalysis' | 'pages' | 'dateModified' | 'updatedAt' | 'status' | 'sfStatus' | 'sfLastModified' | 'actions')[] => {
    switch (dashboard.activeSection) {
      case 'new':
        return ['checkbox', 'caseNumber', 'typeOfAnalysis', 'dateModified', 'actions'];
      case 'queue':
        return ['checkbox', 'caseNumber', 'typeOfAnalysis', 'pages', 'status', 'updatedAt', 'actions'];
      case 'approved':
        return ['checkbox', 'caseNumber', 'typeOfAnalysis', 'status', 'updatedAt', 'actions'];
      case 'uploaded':
        return ['caseNumber', 'typeOfAnalysis', 'updatedAt', 'sfStatus', 'sfLastModified', 'actions'];
      case 'returned':
        return ['caseNumber', 'typeOfAnalysis', 'status', 'updatedAt', 'sfStatus', 'sfLastModified', 'actions'];
      case 'issues':
        return ['caseNumber', 'typeOfAnalysis', 'status', 'updatedAt', 'sfStatus', 'sfLastModified', 'actions'];
      case 'other':
        return ['caseNumber', 'typeOfAnalysis', 'updatedAt', 'sfStatus', 'sfLastModified', 'pages', 'actions'];
      default:
        return ['caseNumber', 'typeOfAnalysis', 'status', 'updatedAt', 'actions'];
    }
  };

  // Get the cases to display - use server-side data for uploaded/other sections
  const displayCases = useMemo(() => {
    if (dashboard.activeSection === 'uploaded') {
      return uploadedCases.cases;
    }
    if (dashboard.activeSection === 'other') {
      return otherCases.cases;
    }
    return dashboard.paginatedCases;
  }, [dashboard.activeSection, dashboard.paginatedCases, uploadedCases.cases, otherCases.cases]);

  // Selectable cases for footer
  const selectableCases = useMemo(() => {
    switch (dashboard.activeSection) {
      case 'new':
        return displayCases;
      case 'queue':
        return displayCases.filter((c) => ['downloaded', 'failed'].includes(c.status));
      case 'approved':
        return displayCases.filter((c) => c.status === 'approved');
      default:
        return [];
    }
  }, [dashboard.activeSection, displayCases]);

  const sidebar = (
    <Sidebar
      isCollapsed={isCollapsed}
      onToggle={toggleSidebar}
      activeSection={dashboard.activeSection}
      onSectionChange={dashboard.setActiveSection}
    />
  );

  return (
    <DashboardLayout sidebar={sidebar}>
      <div className="p-6">
        {/* Pipeline Overview */}
        <PipelineOverview
          counts={pipelineCounts}
          activeSection={dashboard.activeSection}
          onSectionClick={dashboard.setActiveSection}
        />

        {/* Quick Stats */}
        <QuickStats
          todayCompleted={quickStats?.today_completed ?? 0}
          weekCompleted={quickStats?.week_completed ?? 0}
          processing={quickStats?.processing ?? 0}
          errors={quickStats?.errors ?? 0}
        />

        {/* Queue Header */}
        <QueueHeader
          section={dashboard.activeSection}
          totalCount={dashboard.activeSection === 'uploaded' ? uploadedCases.total : dashboard.activeSection === 'other' ? otherCases.total : dashboard.totalFiltered}
          searchQuery={dashboard.activeSection === 'uploaded' ? uploadedCases.search : dashboard.activeSection === 'other' ? otherCases.search : dashboard.searchQuery}
          onSearchChange={dashboard.activeSection === 'uploaded' ? uploadedCases.setSearch : dashboard.activeSection === 'other' ? otherCases.setSearch : dashboard.setSearchQuery}
          onRefresh={dashboard.activeSection === 'uploaded' ? uploadedCases.refresh : dashboard.activeSection === 'other' ? otherCases.refresh : handleRefresh}
          refreshCountdown={dashboard.activeSection === 'uploaded' || dashboard.activeSection === 'other' ? undefined : refreshCountdown}
          issueFilter={dashboard.issueFilter}
          onIssueFilterChange={dashboard.setIssueFilter}
          issueCounts={dashboard.issueCounts}
          selectedCount={dashboard.selectedCases.size}
          onDownloadSelected={handleDownloadSelected}
          onTranslateSelected={handleTranslateAllClick}
          onUploadSelected={handleUploadSelected}
          onClearSelection={dashboard.clearSelection}
          isDownloading={Array.from(dashboard.selectedCases).some((cn) => isDownloading(cn))}
          isTranslating={Array.from(dashboard.selectedCases).some((cn) => isTranslating(cn))}
          isUploading={Array.from(dashboard.selectedCases).some((cn) => isUploading(cn))}
        />

        {/* Main Content Area */}
        <div className="bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
          {/* Loading state for New in SF */}
          {dashboard.activeSection === 'new' && isFetchingSalesforce && displayCases.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <span className="inline-block w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
              <p>Fetching cases from Salesforce...</p>
            </div>
          ) : dashboard.activeSection === 'uploaded' && uploadedCases.isLoading && displayCases.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <span className="inline-block w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
              <p>Loading uploaded cases...</p>
            </div>
          ) : dashboard.activeSection === 'other' && otherCases.isLoading && displayCases.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <span className="inline-block w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
              <p>Loading other cases...</p>
            </div>
          ) : (
            <CaseTable
              cases={displayCases}
              showCheckbox={['new', 'queue', 'approved'].includes(dashboard.activeSection)}
              columns={getColumns()}
              selectedCases={dashboard.selectedCases}
              onSelectCase={dashboard.toggleSelection}
              onSelectAll={(sel) => dashboard.selectAll(selectableCases, sel)}
              onDownload={handleDownload}
              onTranslate={handleTranslate}
              onUpload={handleUpload}
              onRestart={handleRestartClick}
              isDownloading={isDownloading}
              isTranslating={isTranslating}
              isUploading={isUploading}
              isRestarting={isRestarting}
              sfStatusMap={sfStatusMap}
              maxHeight="calc(100vh - 380px)"
            />
          )}

          {/* Queue Footer - show pagination for uploaded and other sections */}
          {dashboard.activeSection === 'uploaded' && (
            <QueueFooter
              section={dashboard.activeSection}
              currentPage={uploadedCases.page}
              totalPages={uploadedCases.totalPages}
              pageSize={uploadedCases.pageSize}
              totalItems={uploadedCases.total}
              onPageChange={uploadedCases.setPage}
              onPageSizeChange={uploadedCases.setPageSize}
            />
          )}
          {dashboard.activeSection === 'other' && (
            <QueueFooter
              section={dashboard.activeSection}
              currentPage={otherCases.page}
              totalPages={otherCases.totalPages}
              pageSize={otherCases.pageSize}
              totalItems={otherCases.total}
              onPageChange={otherCases.setPage}
              onPageSizeChange={otherCases.setPageSize}
            />
          )}
        </div>
      </div>

      {/* Translation Confirmation Dialog */}
      <ConfirmDialog
        isOpen={translateConfirm.isOpen}
        title="Confirm Translation"
        message={
          <div>
            <p className="mb-2 flex items-center gap-1">
              You are about to translate{' '}
              <strong className="mx-1">{translateConfirm.casesToTranslate.length}</strong> case
              {translateConfirm.casesToTranslate.length !== 1 ? 's' : ''}.
              <span className="relative group">
                <svg
                  className="w-4 h-4 text-gray-400 cursor-help"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                  Up to 100 translations run in parallel.
                  <br />
                  Additional cases queue automatically.
                </span>
              </span>
            </p>
            {translateConfirm.newCount > 0 && (
              <p className="text-gray-600">
                • {translateConfirm.newCount} new translation
                {translateConfirm.newCount !== 1 ? 's' : ''}
              </p>
            )}
            {translateConfirm.redoCount > 0 && (
              <p className="text-amber-600 font-medium">
                • {translateConfirm.redoCount} will be re-translated (existing translations will be
                overwritten)
              </p>
            )}
          </div>
        }
        confirmLabel="Translate"
        onConfirm={handleTranslateConfirm}
        onCancel={handleTranslateCancel}
      />

      {/* Restart Confirmation Dialog */}
      <ConfirmDialog
        isOpen={restartConfirm.isOpen}
        title="Confirm Restart"
        message={
          <div>
            <p className="mb-2">
              Are you sure you want to restart case <strong>{restartConfirm.caseNumber}</strong>?
            </p>
            <p className="text-red-600 font-medium">
              This will delete all existing files and re-download the case from Salesforce.
            </p>
          </div>
        }
        confirmLabel="Restart"
        confirmVariant="danger"
        onConfirm={handleRestartConfirm}
        onCancel={handleRestartCancel}
      />
    </DashboardLayout>
  );
}
